/**
 * AIPollingContext - React Context wrapper for AIPollingService
 * 
 * Provides React hooks for accessing the global AI polling service,
 * with automatic subscription management and cleanup.
 */

'use client'

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { AIPollingService, SessionProgress, ActiveSessionInfo, type SessionMode } from '@/lib/services/AIPollingService'

interface AIPollingContextValue {
    // Get all active sessions
    activeSessions: ActiveSessionInfo[]
    
    // Start/stop polling (onUpdate is optional; pass to ensure callback is attached when replacing an in-progress run)
    startPolling: (sessionId: string, mode: SessionMode, sessionName?: string, onUpdate?: (progress: SessionProgress) => void, listId?: number) => void
    stopPolling: (sessionId: string) => void
    
    // Get specific session progress
    getSessionProgress: (sessionId: string) => SessionProgress | null
    
    // Notification settings
    notificationsEnabled: boolean
    setNotificationsEnabled: (enabled: boolean) => void
}

const AIPollingContext = createContext<AIPollingContextValue | undefined>(undefined)

export function AIPollingProvider({ children }: { children: ReactNode }) {
    const [activeSessions, setActiveSessions] = useState<ActiveSessionInfo[]>([])
    const [notificationsEnabled, setNotificationsEnabledState] = useState(() => 
        AIPollingService.areNotificationsEnabled()
    )

    // Subscribe to global session events
    useEffect(() => {
        const handleEvent = () => {
            // Update active sessions list whenever any session changes
            setActiveSessions(AIPollingService.getActiveSessions())
        }

        AIPollingService.subscribeToAllSessions(handleEvent)

        // Initial load
        setActiveSessions(AIPollingService.getActiveSessions())

        return () => {
            AIPollingService.unsubscribeFromAllSessions(handleEvent)
        }
    }, [])

    const startPolling = useCallback((sessionId: string, mode: SessionMode, sessionName?: string, onUpdate?: (progress: SessionProgress) => void, listId?: number) => {
        AIPollingService.startPolling(sessionId, mode, sessionName, onUpdate, listId)
        setActiveSessions(AIPollingService.getActiveSessions())
    }, [])

    const stopPolling = useCallback((sessionId: string) => {
        AIPollingService.stopPolling(sessionId)
        setActiveSessions(AIPollingService.getActiveSessions())
    }, [])

    const getSessionProgress = useCallback((sessionId: string) => {
        return AIPollingService.getSessionProgress(sessionId)
    }, [])

    const setNotificationsEnabled = useCallback((enabled: boolean) => {
        AIPollingService.setNotificationsEnabled(enabled)
        setNotificationsEnabledState(enabled)
    }, [])

    const value: AIPollingContextValue = {
        activeSessions,
        startPolling,
        stopPolling,
        getSessionProgress,
        notificationsEnabled,
        setNotificationsEnabled
    }

    return (
        <AIPollingContext.Provider value={value}>
            {children}
        </AIPollingContext.Provider>
    )
}

/**
 * Hook to access AI polling context
 */
export function useAIPollingContext() {
    const context = useContext(AIPollingContext)
    if (!context) {
        throw new Error('useAIPollingContext must be used within AIPollingProvider')
    }
    return context
}

/**
 * Hook to poll a specific session with automatic subscription management
 * 
 * @param sessionId - The session to poll (null/undefined to skip)
 * @param mode - 'people' or 'companies'
 * @param options - Configuration options
 * @returns Current progress and control methods
 */
export function useAIPolling(
    sessionId: string | null | undefined,
    mode: SessionMode,
    options?: {
        sessionName?: string
        autoStart?: boolean
        onComplete?: (progress: SessionProgress) => void
        onUpdate?: (progress: SessionProgress) => void
    }
) {
    const { startPolling, stopPolling, getSessionProgress } = useAIPollingContext()
    const [progress, setProgress] = useState<SessionProgress | null>(null)
    const [isPolling, setIsPolling] = useState(false)

    const autoStart = options?.autoStart ?? false
    const onComplete = options?.onComplete
    const onUpdate = options?.onUpdate
    const sessionName = options?.sessionName

    // Track if we've completed to prevent duplicate onComplete calls
    const [hasCompleted, setHasCompleted] = useState(false)

    // Subscribe to session updates
    useEffect(() => {
        if (!sessionId) {
            setProgress(null)
            setIsPolling(false)
            return
        }

        // Check if already polling
        const existingProgress = getSessionProgress(sessionId)
        if (existingProgress) {
            setProgress(existingProgress)
            setIsPolling(!existingProgress.isComplete)
        }

        // Create callback for updates
        const handleUpdate = (updatedProgress: SessionProgress) => {
            setProgress(updatedProgress)
            setIsPolling(!updatedProgress.isComplete)

            // Call onUpdate callback
            if (onUpdate) {
                onUpdate(updatedProgress)
            }

            // Call onComplete callback (only once)
            if (updatedProgress.isComplete && !hasCompleted && onComplete) {
                setHasCompleted(true)
                onComplete(updatedProgress)
            }
        }

        // Subscribe to updates
        AIPollingService.subscribeToSession(sessionId, handleUpdate)

        // Auto-start if configured
        if (autoStart && existingProgress === null) {
            startPolling(sessionId, mode, sessionName)
            setIsPolling(true)
        }

        return () => {
            AIPollingService.unsubscribeFromSession(sessionId, handleUpdate)
        }
    }, [sessionId, mode, autoStart, sessionName, onComplete, onUpdate, getSessionProgress, startPolling, hasCompleted])

    const start = useCallback(() => {
        if (!sessionId) return
        startPolling(sessionId, mode, sessionName)
        setIsPolling(true)
        setHasCompleted(false)
    }, [sessionId, mode, sessionName, startPolling])

    const stop = useCallback(() => {
        if (!sessionId) return
        stopPolling(sessionId)
        setIsPolling(false)
    }, [sessionId, stopPolling])

    return {
        progress,
        isPolling,
        start,
        stop
    }
}

/**
 * Hook to monitor all active AI sessions
 * Useful for displaying a global indicator
 */
export function useActiveAISessions() {
    const { activeSessions, stopPolling } = useAIPollingContext()
    
    return {
        sessions: activeSessions,
        count: activeSessions.length,
        hasActiveSessions: activeSessions.length > 0,
        stopPolling
    }
}
